import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getDashboard } from '../../services/adminService';
import './AdminPage.css';

const STATUS_LABELS = {
  trial: 'Trial',
  active: 'Ativo',
  suspended: 'Suspenso',
  cancelled: 'Cancelado',
};

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('pt-BR');
}

function StatusBadge({ status }) {
  return <span className={`adm-badge adm-badge-${status}`}>{STATUS_LABELS[status] || status}</span>;
}

export default function AdminDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getDashboard()
      .then(setData)
      .catch(e => setError(e.response?.data?.message || 'Erro ao carregar dashboard.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="adm-loading">Carregando dashboard...</div>;
  if (error) return <div className="adm-alert adm-alert-danger">{error}</div>;

  const { totals, mrr, payments_this_month: pm, trials_expiring_soon, recent_companies, overdue_payments } = data;

  return (
    <div>
      <div className="adm-page-header">
        <h2>Backoffice — Dashboard</h2>
        <Link to="/admin/companies" className="adm-btn adm-btn-primary">
          + Nova Empresa
        </Link>
      </div>

      {/* KPI Cards */}
      <div className="adm-kpi-grid">
        <div className="adm-kpi-card">
          <div className="kpi-label">Empresas Ativas</div>
          <div className="kpi-value">{totals.active}</div>
          <div className="kpi-sub">de {totals.companies} total</div>
        </div>
        <div className="adm-kpi-card">
          <div className="kpi-label">MRR</div>
          <div className="kpi-value">{formatCurrency(mrr)}</div>
          <div className="kpi-sub">Receita mensal recorrente</div>
        </div>
        <div className="adm-kpi-card">
          <div className="kpi-label">Pagamentos Pendentes</div>
          <div className="kpi-value">{pm.pending}</div>
          <div className="kpi-sub">{pm.paid} pagos · {pm.failed} falhos este mês</div>
        </div>
        <div className="adm-kpi-card">
          <div className="kpi-label">Trials Expirando</div>
          <div className="kpi-value">{trials_expiring_soon}</div>
          <div className="kpi-sub">nos próximos 7 dias</div>
        </div>
        <div className="adm-kpi-card">
          <div className="kpi-label">Em Trial</div>
          <div className="kpi-value">{totals.trial}</div>
        </div>
        <div className="adm-kpi-card">
          <div className="kpi-label">Suspensos</div>
          <div className="kpi-value">{totals.suspended}</div>
        </div>
      </div>

      {/* Overdue Payments Alert */}
      {overdue_payments.length > 0 && (
        <div className="adm-alert adm-alert-warning" style={{ marginBottom: '1.5rem' }}>
          <strong>Atenção:</strong> {overdue_payments.length} pagamento(s) em atraso.
        </div>
      )}

      <div className="adm-two-col">
        {/* Recent Companies */}
        <div className="adm-card">
          <h3>Últimas Empresas</h3>
          {recent_companies.length === 0 ? (
            <div className="adm-empty"><div className="adm-empty-icon">🏢</div><p>Nenhuma empresa</p></div>
          ) : (
            <div className="adm-table-wrapper">
              <table className="adm-table adm-table--cards">
                <thead>
                  <tr>
                    <th>Empresa</th>
                    <th>Status</th>
                    <th>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {recent_companies.map(c => (
                    <tr key={c.id} className="clickable">
                      <td data-label="Empresa">
                        <Link to={`/admin/companies/${c.id}`} style={{ color: '#4f46e5', textDecoration: 'none', fontWeight: 500 }}>
                          {c.name}
                        </Link>
                      </td>
                      <td data-label="Status">
                        <StatusBadge status={c.subscription_status || 'trial'} />
                      </td>
                      <td data-label="Valor">{formatCurrency(c.subscription_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ marginTop: '0.75rem' }}>
            <Link to="/admin/companies" className="adm-btn adm-btn-secondary adm-btn-sm">
              Ver todas
            </Link>
          </div>
        </div>

        {/* Overdue Payments */}
        <div className="adm-card">
          <h3>Pagamentos Atrasados</h3>
          {overdue_payments.length === 0 ? (
            <div className="adm-empty"><div className="adm-empty-icon">✅</div><p>Sem atrasos</p></div>
          ) : (
            <div className="adm-table-wrapper">
              <table className="adm-table adm-table--cards">
                <thead>
                  <tr>
                    <th>Empresa</th>
                    <th>Vencimento</th>
                    <th>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {overdue_payments.map(p => (
                    <tr key={p.id}>
                      <td data-label="Empresa">
                        <Link to={`/admin/companies/${p.company_id}`} style={{ color: '#4f46e5', textDecoration: 'none' }}>
                          {p.company_name}
                        </Link>
                      </td>
                      <td data-label="Vencimento" style={{ color: '#dc2626' }}>{formatDate(p.due_date)}</td>
                      <td data-label="Valor">{formatCurrency(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
